import { Fragment } from 'react'
import { TriangleAlert } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { resourcesNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  BUCKETS,
  CAPACITY_CHROME,
  CAPACITY_COPY,
  CAPACITY_DENSITY_NOTE,
  DECIDE_SUFFIX,
  DECISIONS,
  DECISIONS_LABEL,
  HEAT_ROWS,
  KPIS,
  SQUEEZE_ANCHOR_ID,
  WEEKS,
  bucketFor,
  type HeatRow,
  type Segment,
} from '@/demo/content/capacity'
import { cn } from '@/lib/utils'

const copy = CAPACITY_COPY

/** Prose in which some words are real destinations. */
function Prose({ segments }: { segments: readonly Segment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.kind === 'text' ? (
          <Fragment key={`text-${index}`}>{segment.text}</Fragment>
        ) : (
          <Link
            key={`link-${index}-${segment.href}`}
            to={segment.href}
            className="font-medium text-accent-foreground underline underline-offset-2 hover:text-foreground"
          >
            {segment.label}
          </Link>
        )
      )}
    </>
  )
}

function HeatCell({ loadPct }: { loadPct: number }) {
  const bucket = bucketFor(loadPct)
  return (
    <span
      className={cn(
        'v-figure block rounded-[4px] px-1 py-1.5 text-center text-xs font-medium tabular-nums',
        bucket.className
      )}
    >
      {copy.cellLabel(loadPct)}
      {bucket.cellSuffix && (
        <span className="ml-0.5 text-[10px] font-semibold uppercase tracking-[0.04em]">
          {bucket.cellSuffix}
        </span>
      )}
      <span className="sr-only"> · {bucket.srLabel}</span>
    </span>
  )
}

function PersonHeatRow({ row }: { row: HeatRow }) {
  const { weekly = [] } = row
  return (
    <TableRow>
      <TableHead
        scope="row"
        className="h-auto py-2 pl-2 pr-4 align-middle font-normal whitespace-nowrap"
      >
        <span className="flex items-center gap-2">
          <span className="leading-tight">
            <Link
              to={row.href}
              className="block font-medium text-foreground underline-offset-2 hover:underline"
            >
              {row.name}
            </Link>
            <span className="block text-xs text-muted-foreground">
              {row.role}
            </span>
          </span>
          <Badge
            variant="outline"
            className="ml-auto font-normal text-ink-soft"
          >
            {row.office}
          </Badge>
        </span>
      </TableHead>

      {WEEKS.map((week, index) => (
        <TableCell key={week.key} className="p-1">
          <HeatCell loadPct={weekly[index] ?? 0} />
        </TableCell>
      ))}
    </TableRow>
  )
}

/**
 * 1.3 Capacity forecast — the 12-week load of the tightest and idlest people
 * in the group, with pipeline demand carried as shadow demand so a hiring or
 * training decision is taken in W35 rather than discovered in W41.
 */
export default function CapacityForecastPage() {
  return (
    <AppShell
      moduleLabel={CAPACITY_CHROME.moduleLabel}
      breadcrumb={CAPACITY_CHROME.breadcrumb}
      contextPill={CAPACITY_CHROME.contextPill}
      nav={resourcesNav}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{copy.title}</h1>
      {/* PX-W6.2 (AC-8): the demo badge — this surface renders seed content and stays network-quiet. */}
      <ConceptBadge />
        <p className="text-sm text-muted-foreground">{copy.lede}</p>
      </header>

      <div className="mt-5 grid grid-cols-2 gap-3 xl:grid-cols-4">
        {KPIS.map((kpi) => (
          <KpiCard
            key={kpi.key}
            label={kpi.label}
            className={cn(kpi.emphasis && 'border-blocked-bg bg-blocked-bg')}
            figure={
              kpi.emphasis ? (
                <span className="flex items-center gap-2 text-rose-ink">
                  <TriangleAlert className="h-5 w-5 shrink-0" aria-hidden />
                  {kpi.figure}
                </span>
              ) : (
                kpi.figure
              )
            }
            qualifier={
              kpi.anchorHref ? (
                <a
                  href={kpi.anchorHref}
                  className="font-medium text-rose-ink underline underline-offset-2"
                >
                  {kpi.qualifier}
                </a>
              ) : (
                kpi.qualifier
              )
            }
          />
        ))}
      </div>

      <section className="mt-6" aria-label={copy.tableLabel}>
        <SectionLabel>{copy.tableLabel}</SectionLabel>

        <div className="mt-2 rounded-lg border border-border bg-card">
          <Table className="table-fixed">
            <TableCaption className="sr-only">{copy.tableCaption}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead
                  scope="col"
                  className="w-[15rem] text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground"
                >
                  {copy.personColumn}
                </TableHead>
                {WEEKS.map((week) => (
                  <TableHead
                    key={week.key}
                    scope="col"
                    id={week.isSqueeze ? SQUEEZE_ANCHOR_ID : undefined}
                    className={cn(
                      'h-auto px-1 py-2 text-center align-bottom text-xs font-semibold tabular-nums text-foreground',
                      week.isSqueeze && 'bg-blocked-bg/60',
                      week.isNow && 'bg-muted'
                    )}
                  >
                    <span className="v-figure block">{week.label}</span>
                    {week.isNow && (
                      <span className="block text-[10px] font-medium lowercase text-muted-foreground">
                        {copy.nowMarker}
                      </span>
                    )}
                    {week.isSqueeze && (
                      <span className="block text-[10px] font-medium lowercase text-blocked">
                        {copy.squeezeMarker}
                      </span>
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {HEAT_ROWS.map((row) => (
                <PersonHeatRow key={row.personId} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <SectionLabel>{copy.legendLabel}</SectionLabel>
          {BUCKETS.map((bucket) => (
            <span
              key={bucket.key}
              className="flex items-center gap-2 text-xs text-ink-soft"
            >
              <span
                aria-hidden
                className={cn(
                  'v-figure rounded-[4px] px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
                  bucket.className
                )}
              >
                {bucket.range}
              </span>
              {bucket.label}
            </span>
          ))}
        </div>
      </section>

      <section
        className="mt-6 rounded-lg border border-border bg-accent px-4 py-4"
        aria-label={DECISIONS_LABEL}
      >
        <SectionLabel className="text-accent-foreground">
          {DECISIONS_LABEL}
        </SectionLabel>
        <ul className="mt-3 grid gap-3 xl:grid-cols-3">
          {DECISIONS.map((decision) => (
            <li
              key={decision.key}
              className="rounded-md border border-accent-foreground/20 bg-card px-3 py-3"
            >
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="font-serif text-base font-semibold text-foreground">
                  {decision.title}
                </h2>
                <StatusPill tone="proposed">{decision.tag}</StatusPill>
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">
                <Prose segments={decision.body} />
                <Link
                  to={DECIDE_SUFFIX.href}
                  className="font-medium whitespace-nowrap text-accent-foreground underline underline-offset-2 hover:text-foreground"
                >
                  {DECIDE_SUFFIX.arrow}
                </Link>
              </p>
            </li>
          ))}
        </ul>
      </section>

      <DensityNote>
        <Prose segments={CAPACITY_DENSITY_NOTE} />
      </DensityNote>
    </AppShell>
  )
}
